import type React from "react";
import { twJoin } from "tailwind-merge";

import type { ChatDto, ChatReplyDto } from "@/api/types";
import { SystemChatMessage } from "@/components/ChatWindow/SystemChatMessage";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { PaperclipIcon } from "@/components/Icons/PaperclipIcon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { NumericBadge } from "@/design-system/NumericBadge";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useTranslation } from "@/translations";

import { PinFilled01Icon } from "../Icons/PinFilled01Icon";

interface ChatPreviewProps {
  chat: ChatDto;
  showLatestReplyDate?: boolean;
  showUnreadRepliesCount?: boolean;
  showStatus?: boolean;
}

export function ChatPreview({
  chat,
  showLatestReplyDate,
  showUnreadRepliesCount,
  showStatus,
}: ChatPreviewProps): React.ReactNode {
  const { t } = useTranslation();
  const isGroupChat = chat.chatType === "group" && chat.groupChatInfo;

  const getChatName = (chat: ChatDto) => {
    if (chat.chatType === "single") {
      return chat.privateChatInfo!.receiver.fullName;
    } else {
      return chat.groupChatInfo!.name;
    }
  };

  const chatName = getChatName(chat);

  return (
    <article className="flex w-full min-w-0 flex-wrap items-center justify-between gap-x-5 gap-y-4">
      <header className="flex min-w-0 flex-1 items-center gap-space-sm">
        {/* Chat avatar */}
        {!isGroupChat && (
          <UserAvatar
            image={chat.privateChatInfo!.receiver.avatar}
            isUserDeleted={!!chat.privateChatInfo!.receiver.deletedAt}
          />
        )}
        {isGroupChat && chat.groupChatInfo?.image && <UserAvatar image={chat.groupChatInfo.image} />}
        {isGroupChat && !chat.groupChatInfo?.image && (
          <span className="flex size-control-md shrink-0 rounded-full border border-border-default bg-background-default p-space-xs">
            <span className="flex size-full items-center justify-center">
              <Users01Icon tone="secondary" size={25} />
            </span>
          </span>
        )}
        <section className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* Chat name */}
          <h2 className="flex flex-1 items-center gap-space-sm">
            <span
              className={twJoin(
                "max-w-full truncate text-body-emphasis",
                !isGroupChat && chat.privateChatInfo!.receiver.deletedAt ? "text-text-secondary" : "text-text-primary",
              )}
              data-testid="chat-name"
            >
              {chatName}
            </span>
          </h2>
          {/* Chat latest reply */}
          {chat.latestReply && (
            <p className="flex-1 truncate">
              <ChatPreviewLatestReply
                name={chat.groupChatInfo?.name}
                type={chat.chatType}
                latestReply={chat.latestReply}
              />
            </p>
          )}
        </section>
      </header>
      {((showLatestReplyDate && chat.latestReply?.sentAt) ||
        (showUnreadRepliesCount && chat.unreadReplies > 0) ||
        (showStatus && chat.pinnedAt)) && (
        <aside className="flex shrink-0 items-center justify-between gap-space-sm @md:justify-start">
          {showLatestReplyDate && chat.latestReply?.sentAt && (
            <span
              className={twJoin(
                "w-full truncate text-right text-overline @md:text-caption",
                chat.hasUnreadReply ? "text-text-brand" : "text-text-secondary",
              )}
            >
              <FormattedDate format="datetimeShortRelative" date={chat.latestReply.sentAt} />
            </span>
          )}
          <span className="flex gap-space-xs">
            {showUnreadRepliesCount && chat.unreadReplies > 0 && (
              <NumericBadge variant="filled" count={chat.unreadReplies} />
            )}
            {showStatus && chat.pinnedAt && (
              <span
                role="img"
                aria-label={t("component.chat-list-item.status.pinned")}
                data-testid="pinned-chat-indicator"
              >
                <PinFilled01Icon size={20} tone="tertiary" />
              </span>
            )}
          </span>
        </aside>
      )}
    </article>
  );
}

type ChatListItemLatestReplyPreviewProps = {
  name: string | undefined;
  type: ChatDto["chatType"];
  latestReply: ChatReplyDto;
};

const ChatPreviewLatestReply = ({ name, type, latestReply }: ChatListItemLatestReplyPreviewProps) => {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const isSystemReply = latestReply.replyType?.includes("system");
  const isMemberReply = latestReply.replyType?.includes("member");
  const isAttachmentOnlyReply = !latestReply.text && (latestReply.image || latestReply.videos);

  return (
    <span className="flex w-full items-center text-left">
      {isSystemReply && <SystemChatMessage chatName={name} chatMessage={latestReply} presentation="preview" />}
      {isMemberReply && latestReply.text && (
        <span className="sentry-block w-full truncate text-caption text-text-secondary">
          {type === "group" && (
            <span className="text-caption-emphasis">{`${latestReply.author?.id === sessionUser.id ? t("page.chats.list.self-message") : latestReply.author?.fullName}: `}</span>
          )}
          {latestReply.text}
        </span>
      )}
      {isMemberReply && isAttachmentOnlyReply && (
        <span className="flex w-full items-center gap-space-xs truncate text-caption text-text-secondary">
          {type === "group" && (
            <span className="text-overline-emphasis">{`${latestReply.author?.id === sessionUser.id ? t("page.chats.list.self-message") : latestReply.author?.fullName}: `}</span>
          )}
          <PaperclipIcon tone="secondary" />
          <span className="text-caption">{t("page.chats.attachment")}</span>
        </span>
      )}
    </span>
  );
};
